/**
 * Queue for batching and syncing missing translations to the backend
 * Uses debouncing (500ms default) and batching (50 items default)
 */

import type { EventEmitter } from '../base/event-emitter.js';
import type { IAsyncRepository } from '../interfaces/repository.interface.js';
import { logger } from '../utils/logger.js';

export interface MissingTranslationItem {
  key: string;
  fallbackValue: string;
  styles?: Record<string, string[]>;
  locale: string;
  namespace: string;
}

export interface MissingTranslationQueueConfig {
  enabled: boolean;
  debounceMs: number;
  batchSize: number;
}

const COOLDOWN_MS = 60_000; // 60s cooldown before re-syncing same key

export class MissingTranslationQueue {
  private queue: Map<string, MissingTranslationItem> = new Map();
  private debounceTimer: NodeJS.Timeout | null = null;
  private repository: IAsyncRepository;
  private config: MissingTranslationQueueConfig;
  private eventEmitter: EventEmitter | null;
  private recentlySynced: Map<string, number> = new Map();

  constructor(
    repository: IAsyncRepository,
    config: MissingTranslationQueueConfig,
    eventEmitter?: EventEmitter
  ) {
    this.repository = repository;
    this.config = config;
    this.eventEmitter = eventEmitter ?? null;

    if (!config.enabled) {
      logger.debug('MissingTranslationQueue is disabled');
    } else {
      logger.debug(
        `MissingTranslationQueue: debounce=${config.debounceMs}ms, batchSize=${config.batchSize}`
      );
    }
  }

  /**
   * Add a missing translation to the queue
   * Triggers debounce timer or immediate flush if batch size reached
   */
  add(item: MissingTranslationItem): void {
    if (!this.config.enabled) return;

    // Use individual cache key format: locale:namespace:key
    const cacheKey = `${item.locale}:${item.namespace}:${item.key}`;

    // Skip keys synced within cooldown period (prevents re-sync loops)
    const syncedAt = this.recentlySynced.get(cacheKey);
    if (syncedAt && Date.now() - syncedAt < COOLDOWN_MS) {
      logger.debug(`Skipping recently synced key: ${cacheKey}`);
      return;
    }

    // Add to queue (overwrites if already exists)
    this.queue.set(cacheKey, item);

    logger.debug(
      `Missing translation queued: ${cacheKey} (queue size: ${this.queue.size})`
    );

    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Check if batch size limit reached
    if (this.queue.size >= this.config.batchSize) {
      logger.debug(`Batch size reached (${this.config.batchSize}), flushing`);
      this.flush();
      return;
    }

    // Start new debounce timer
    this.debounceTimer = setTimeout(() => {
      this.flush();
    }, this.config.debounceMs);
  }

  /**
   * Flush the queue - send all queued items to the backend
   * Fire-and-forget pattern (async but not awaited)
   */
  flush(): void {
    if (this.queue.size === 0) return;

    // Clean up expired cooldown entries
    const now = Date.now();
    for (const [key, ts] of this.recentlySynced) {
      if (now - ts >= COOLDOWN_MS) this.recentlySynced.delete(key);
    }

    // Convert queue to array, preserving styles
    const batch = Array.from(this.queue.entries()).map(([cacheKey, item]) => ({
      key: cacheKey,
      value: item.fallbackValue,
      styles: item.styles,
    }));

    const batchSize = batch.length;

    // Clear queue
    this.queue.clear();

    // Clear timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    logger.debug(`Flushing ${batchSize} missing translations`);

    // Fire-and-forget sync (no await)
    this.syncBatch(batch).catch(error => {
      logger.warn(`Failed to sync ${batchSize} missing translations:`, error);
    });
  }

  /**
   * Send batch to backend via repository
   * Uses repository's retry logic from makeRequest()
   */
  private async syncBatch(
    batch: Array<{ key: string; value: string; styles?: Record<string, string[]> }>
  ): Promise<void> {
    try {
      // Use setBatchContentUnits when any item has styles
      const hasStyles = batch.some(item => item.styles && Object.keys(item.styles).length > 0);

      if (hasStyles && 'setBatchContentUnits' in this.repository) {
        const contentUnitBatch = batch.map(item => ({
          key: item.key,
          value: { text: item.value, styles: item.styles ?? {} },
        }));
        await (
          this.repository as IAsyncRepository & {
            setBatchContentUnits: (
              items: Array<{ key: string; value: { text: string; styles: Record<string, string[]> } }>
            ) => Promise<void>;
          }
        ).setBatchContentUnits(contentUnitBatch);
        logger.debug(`Synced ${batch.length} missing translations (ContentUnit)`);
      } else if ('setBatch' in this.repository) {
        // Plain string batch (no styles)
        const plainBatch = batch.map(item => ({ key: item.key, value: item.value }));
        await (
          this.repository as IAsyncRepository & {
            setBatch: (
              items: Array<{ key: string; value: string }>
            ) => Promise<void>;
          }
        ).setBatch(plainBatch);
        logger.debug(`Synced ${batch.length} missing translations`);
      } else {
        // Fallback: sync individually (parallel)
        logger.warn(
          'Repository does not support setBatch, syncing individually'
        );
        await this.syncIndividually(batch);
      }
      // Record synced keys for cooldown
      const now = Date.now();
      const syncedKeys: string[] = [];
      for (const item of batch) {
        this.recentlySynced.set(item.key, now);
        syncedKeys.push(item.key);
      }

      // Emit sync:batch:complete event
      if (this.eventEmitter) {
        this.eventEmitter.emit('sync:batch:complete', {
          payload: { count: batch.length, keys: syncedKeys },
        });
      }
    } catch (error) {
      logger.error('Batch sync failed:', error);
      throw error;
    }
  }

  /**
   * Fallback: sync items individually in parallel
   */
  private async syncIndividually(
    batch: Array<{ key: string; value: string }>
  ): Promise<void> {
    const syncPromises = batch.map(async item => {
      try {
        await this.repository.set(item.key, item.value);
        logger.debug(`Synced: ${item.key}`);
      } catch (error) {
        logger.warn(`Failed to sync ${item.key}:`, error);
      }
    });

    await Promise.allSettled(syncPromises);
  }

  /**
   * Get current queue size (for debugging)
   */
  getQueueSize(): number {
    return this.queue.size;
  }

  /**
   * Get queue stats (for debugging)
   */
  getStats(): {
    queueSize: number;
    enabled: boolean;
    debounceMs: number;
    batchSize: number;
  } {
    return {
      queueSize: this.queue.size,
      enabled: this.config.enabled,
      debounceMs: this.config.debounceMs,
      batchSize: this.config.batchSize,
    };
  }

  /**
   * Cleanup - clear timer and queue
   */
  destroy(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Discard to avoid blocking during shutdown
    this.queue.clear();
    this.recentlySynced.clear();

    logger.debug('MissingTranslationQueue destroyed');
  }
}
